import React, {useEffect, useRef, useState} from 'react';
import FormBlock from "../../formComponents/FormBlock";
import BaseSimpleItemEditorForm from "../BaseSimplePage/BaseSimpleItemEditorForm";
import FormFullNameInput from "../../formComponents/FormFullNameInput";
import FormLifeYearsInput from "../../formComponents/FormLifeYearsInput";
import FormLinkedItemSelect from "../../formComponents/FormLinkedItemSelect";
import FormLinkedItemSimpleSelect from "../../formComponents/FormLinkedItemSimpleSelect";
import {allArtistRoles} from "../../data/Enums";
import FormLinkedItemListView from "../../formComponents/FormLinkedItemListView";
import FormImage from "../../formComponents/FormImage";
import FormCheckbox from "../../formComponents/FormCheckbox";
import FormMultilineInput from "../../formComponents/FormMultilineInput";
import FormArtistTagsSelect from "./FormArtistTagsSelect";
import FormUrlInput from "../../formComponents/FormUrlInput";
import {getNonEmptyStringOrDefault} from "../../utils/StringUtil";

const ArtistsItemForm = ({initData, controlRef, initMainRole, allowedRoles}) => {

    const controlRefName = useRef(null);
    const controlRefCountry = useRef(null);
    const controlRefLifeYears = useRef(null);
    const controlRefImage = useRef(null);
    const controlRefMainRole = useRef(null);
    const controlRefTags = useRef(null);
    const controlRefSourceUrl = useRef(null);
    const controlRefSurveyDescription = useRef(null);

    const [selectedMainRole, setSelectedMainRole] = useState(initData?.main_role ?? initMainRole);
    const selectedMainRoleIsAuthor = testMainRoleIsAuthor(selectedMainRole);

    const [isInInitialSurvey, setIsInInitialSurvey] = useState(initData?.is_in_initial_survey ?? false);

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                validate: validate,
                getData: getData,
            };
        }
    });

    function validate() {
        let isValid = true;

        if (!controlRefMainRole.current.validateIsSelected("Нужно выбрать род деятельности!"))
            isValid = false;

        if (!controlRefName.current.validateFirstNameNotEmpty("Нужно указать имя!"))
            isValid = false;

        if (!controlRefName.current.validateLengths(255))
            isValid = false;

        if (selectedMainRoleIsAuthor && !controlRefCountry.current.validateIsSelected("Нужно выбрать страну!"))
            isValid = false;

        if (selectedMainRoleIsAuthor && !controlRefCountry.current.getSelectedId()) {
            isValid = false;
            controlRefCountry.current.setErrorMessage("Нужно выбрать страну!");
        }

        if (selectedMainRoleIsAuthor && !controlRefLifeYears.current.validateInputs()) {
            isValid = false;
            controlRefLifeYears.current.setErrorMessage("Неверно указаны годы жизни!");
        }

        if (controlRefSourceUrl.current.getUrl() !== "") {
            if (!controlRefSourceUrl.current.validateUrl("Неверно указан адрес сайта!"))
                isValid = false;
            else if (!controlRefSourceUrl.current.validateLength(2048))
                isValid = false;
        }

        return isValid;
    }

    function getData() {

        const data = {
            main_role: selectedMainRole,
            first_name: controlRefName.current.getFirstName(),
            middle_name: controlRefName.current.getMiddleName(),
            last_name: controlRefName.current.getLastName(),
            source_url: getNonEmptyStringOrDefault(controlRefSourceUrl.current.getUrl(), null),
        }

        if (selectedMainRoleIsAuthor) {
            data.country_id = controlRefCountry.current.getSelectedId();
            data.year_of_birth = controlRefLifeYears.current.getFromYear();
            data.year_of_death = controlRefLifeYears.current.getTillYear();
            data.image_base64 = controlRefImage.current.isChangedToImage() ? controlRefImage.current.getBase64() : null;
            data.clear_image = controlRefImage.current.isChangedToCleared() ? true : null;
            data.tag_ids = controlRefTags.current.getSelectedIds();
            data.is_in_initial_survey = isInInitialSurvey;
            data.survey_description = isInInitialSurvey ? controlRefSurveyDescription.current.getText() : null;
        }

        return data;
    }

    return (
        <BaseSimpleItemEditorForm>
            <FormBlock title="Основные">
                <FormLinkedItemSimpleSelect
                    controlRef={controlRefMainRole}
                    label="Основной род деятельности"
                    widthLimit={200}
                    initSelectedId={initData?.main_role ?? initMainRole}
                    options={allowedRoles ?? allArtistRoles}
                    tipText={"В зависимости от выбранного основного рода деятельности набор заполняемых полей может меняться"}
                    onSelect={mainRole => {
                        setSelectedMainRole(mainRole);
                        if (!testMainRoleIsAuthor(mainRole))
                            setIsInInitialSurvey(initData?.is_in_initial_survey ?? false);
                    }}
                />
                <FormFullNameInput
                    controlRef={controlRefName}
                    label="Полное имя"
                    widthLimit={350}
                    initValues={initData ? [initData.first_name, initData.middle_name, initData.last_name] : ["", "", ""]}
                />
                {selectedMainRoleIsAuthor && <FormLinkedItemSelect
                    controlRef={controlRefCountry}
                    label="Страна"
                    widthLimit={450}
                    initSelectedId={selectedMainRoleIsAuthor && initData?.country_id === null ? undefined : initData?.country_id}
                    initRefList={initData?.refs?.countries}
                    apiName2="countries"
                    showIds={true}
                />}
                {selectedMainRoleIsAuthor && <FormLifeYearsInput
                    controlRef={controlRefLifeYears}
                    label="Годы жизни"
                    initFromValue={initData?.year_of_birth ?? ""}
                    initTillValue={initData?.year_of_death ?? ""}
                />}
                {selectedMainRoleIsAuthor && <FormImage
                    controlRef={controlRefImage}
                    label="Фото"
                    initImageUrl={initData?.image_url ?? ""}
                    imageViewSize={250}
                    imageCanvasMaxSize={250}
                />}
            </FormBlock>
            {selectedMainRoleIsAuthor && <FormBlock title="Теги">
                <FormArtistTagsSelect
                    controlRef={controlRefTags}
                    //label="Теги"
                    inputWidthLimit={450}
                    selectedItemsAreaWidthLimit={700}
                    initSelectedIds={initData?.tag_ids ?? []}
                    initRefList={initData?.refs?.tags}
                    apiName2="tags"
                    showIds={true}
                />
            </FormBlock>}
            <FormBlock title="Инфо">
                <FormUrlInput
                    controlRef={controlRefSourceUrl}
                    label="Ссылка на источник"
                    widthLimit={500}
                    initValue={initData?.source_url ?? ""}
                    placeholder="https://..."
                />
            </FormBlock>
            {selectedMainRoleIsAuthor && initData &&
                <FormBlock title="Произведения" subTitle='(Для редактирования используйте режим "Произведения")'>
                    <FormLinkedItemListView
                        label=""
                        options={getLiteraryWorkOptions(initData)}
                        selectedItemsAreaType="column"
                        showIds={true}
                    />
                </FormBlock>}
            {selectedMainRoleIsAuthor && <FormBlock title={"Подборка любимых авторов"}>
                <FormCheckbox
                    controlRef={null}
                    label="Входит в список авторов для теста"
                    initValue={initData?.is_in_initial_survey ?? false}
                    onSelect={setIsInInitialSurvey}
                />
                {isInInitialSurvey && <FormMultilineInput
                    controlRef={controlRefSurveyDescription}
                    label="Известные произведения"
                    widthLimit={700}
                    initValue={initData?.survey_description ?? ""}
                    maxVisibleRows={6}
                    placeholder="Произведение1, Произведение2, ..."
                    tipText="Краткий список известных произведений автора, который будет показан на карточке автора"
                />}
            </FormBlock>}
        </BaseSimpleItemEditorForm>
    );
};

function getLiteraryWorkOptions(initData) {
    return initData.literary_work_ids?.map((id) => ({
        id: id,
        name: initData.refs?.literary_works?.[id]?.name ?? "?"
    })) ?? [];
}

function testMainRoleIsAuthor(mainRole) {
    return  mainRole && allArtistRoles.find(x => x.id === mainRole).isAuthor;
}

export default ArtistsItemForm;